// define config
// create instance
// initialize instance
// create handlers for login, logout & response

import store from '@/store'
import {
  AccountInfo,
  AuthenticationResult,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser'

// Config object to be passed to Msal on creation
const msalConfig = {
  auth: {
    clientId: `${process.env.VUE_APP_entra_client_id}`,
    authority: `https://login.microsoftonline.com/${process.env.VUE_APP_entra_tenant_id}`, //  Replace the placeholder with your tenant info
    redirectUri: window.location.origin, // You must register this URI on App Registration. Defaults to window.location.href e.g. http://localhost:3000/
    navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
    postLogoutRedirectURI: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO.
    storeAuthStateInCookie: false, // set this to true if you have to support IEF
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ): void => {
        if (containsPii) {
          return
        }
        /* eslint-disable no-console */
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            return
          case LogLevel.Info:
            console.info(message)
            return
          case LogLevel.Verbose:
            console.debug(message)
            return
          case LogLevel.Warning:
            console.warn(message)
            return
        }
        /* eslint-enable no-console */
      },
    },
  },
}
let accountId: string | undefined = ''

export const msalInstance = new PublicClientApplication(msalConfig)
export const loginRequest = {
  scopes: ['User.Read'],
}
export const logoutRequest = {
  account: msalInstance.getAccount({ homeAccountId: accountId }),
  authority: msalConfig.auth.authority,
}

// selects account and sets active account
async function selectAccount() {
  /**
   * See here for more info on account retrieval:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */
  const currentAccounts = msalInstance.getAllAccounts()

  if (!currentAccounts) {
    return
  } else if (currentAccounts.length > 1) {
    /* eslint-disable no-console */
    console.warn(
      'Multiple accounts detected. Please clear your cache and log in again.'
    )
    /* eslint-enable no-console */
  } else if (currentAccounts.length === 1) {
    accountId = currentAccounts[0].homeAccountId
    msalInstance.setActiveAccount(currentAccounts[0])
    store.commit('SET_ENTRA_USER', msalInstance.getActiveAccount())
  }
}

// Determines if user is authenticated or not, then sets the store
export async function isAuthenticated(
  user: AccountInfo | null
): Promise<boolean> {
  if (user?.idToken) {
    store.commit('SET_ENTRA_USER', user)
    store.commit('SET_USER_AUTH', true)
    return true
  } else {
    return false
  }
}

// retrieves token from api
async function getUserApiToken(user): Promise<void> {
  const result = await msalInstance.acquireTokenSilent({
      scopes: [`api://${process.env.VUE_APP_entra_api_scope}//.default`],
      account: user,
    }),
    accessToken = result.accessToken
  store.commit('SET_ACCESS_TOKEN', accessToken)
  sessionStorage.setItem('apiToken', accessToken)
}

// Handles response from login redirect
export function handleResponse(resp: null | AuthenticationResult): void {
  if (resp !== null) {
    if (store.state.entraUser == null) {
      selectAccount().then(() => {
        isAuthenticated(store.state.entraUser).then(() => {
          if (store.state.isAuthenticated == true) {
            getUserApiToken(store.state.entraUser).then(() => {
              store.dispatch('setUserInfo', store.state.entraUser)
            })
          }
        })
      })
    }
  } else {
    msalInstance.loginRedirect(loginRequest)
  }
}

export async function signIn(): Promise<void> {
  // If your application uses redirects for interaction, handleRedirectPromise must be called and awaited on each page load before determining if a user is signed in or not
  msalInstance.initialize().then(() => {
    msalInstance.handleRedirectPromise().then((r) => {
      handleResponse(r)
    })
  })
}

export async function signOut(): Promise<void> {
  const currentAccount = msalInstance.getActiveAccount()
  msalInstance.logoutRedirect({
    account: currentAccount,
    logoutHint: currentAccount?.idTokenClaims?.login_hint,
  })
}
